import { Injector, runInInjectionContext } from '@angular/core';
import { IsActiveMatchOptions, Route, Router } from '@angular/router';
import { FeatureFlags, TenantStatus } from '@jms/model';
import { EntityManifestService, type EntityManifest } from '@workbuddy/services/entities';
import { listRegisteredEntities } from '@workbuddy/services/entities/entity.manifest.registry';
import { entityListUrl, isEntityListAvailable } from '@workbuddy/ui/v2/blocks/entity.record';
import { isV2EntityEnabled } from '../../legacy/legacy.entity.route.guard';
import { isLegacyEntityDisabled, LEGACY_ENTITY_ROUTES } from '../../legacy/legacy.entity.routes';

/** A manifest-declared list joining a route-declared nav group. */
export interface EntityMenuEntry {
    slug: string;
    group: string;
    order: number;
    route: Route;
}

export interface SideNavBuildInput {
    /** The shell route's children: the route-declared nav. */
    routes: readonly Route[];
    canLoadPath: (path: string) => boolean;
    /** Null until application state loads; nothing is shown before then. */
    tenantStatus: string | null | undefined;
    entityEntries: readonly EntityMenuEntry[];
    legacyNav: readonly LegacyNavItem[];
}

/** A legacy nav path and whether legacy is off for it, from the legacy entity routes. */
export interface LegacyNavItem {
    path: string;
    slug: string;
    hidden: boolean;
}

const NAV_ACTIVE_OPTIONS: IsActiveMatchOptions = { matrixParams: 'ignored', queryParams: 'ignored', paths: 'subset', fragment: 'ignored' };

/**
 * Where a group child links. Manifest entries carry their own absolute `data.link` because they live
 * at `/entity/:slug`, not under the group's path.
 */
export function navChildLink(parent: Route, child: Route): string {

    return child.data?.link ?? `/${parent.path}/${child.path}`;
}

/** A group is active when any child is, including manifest entries whose URL does not start with the group path. */
export function isNavGroupActive(router: Router, route: Route): boolean {

    if (router.isActive(`/${route.path}`, NAV_ACTIVE_OPTIONS)) return true;
    return (route.children ?? []).some(child => router.isActive(navChildLink(route, child), NAV_ACTIVE_OPTIONS));
}

/** Every legacy nav path with whether this tenant has turned it off. */
export function resolveLegacyNav(injector: Injector, flags: Partial<Record<FeatureFlags, boolean>> | undefined): LegacyNavItem[] {

    const manifests = injector.get(EntityManifestService);

    return LEGACY_ENTITY_ROUTES.flatMap(entry => {

        const hidden = isLegacyEntityDisabled(entry, flags, isV2EntityEnabled(manifests, entry.slug));
        return entry.paths.map(path => ({ path, slug: entry.slug, hidden }));
    });
}

/** Menu entries for every registered manifest whose list this tenant and user may open. */
export function resolveEntityMenuEntries(injector: Injector, flags: Partial<Record<FeatureFlags, boolean>> | undefined): EntityMenuEntry[] {

    const manifests = injector.get(EntityManifestService);

    return listRegisteredEntities()
        .map(entity => manifests.getManifest(entity))
        .filter(manifest => isEntityListAvailable(manifest, flags, authorize => runInInjectionContext(injector, authorize)))
        .map(manifest => toEntityMenuEntry(manifest!));
}

function toEntityMenuEntry(manifest: EntityManifest): EntityMenuEntry {

    const menu = manifest.menu!;
    const link = entityListUrl(manifest.slug)!;

    return {
        slug: manifest.slug!,
        group: menu.group,
        order: menu.order,
        route: {
            // Unique among siblings (route-declared children never start with `entity/`), and correct when
            // the group collapses to this single child and the path becomes the top-level link.
            path: link.slice(1),
            data: {
                title: menu.label,
                icon: menu.icon,
                link,
                badge: menu.badge,
                restrictions: [TenantStatus.Ready],
            },
        },
    };
}

/**
 * The side nav, built from the shell's route children plus manifest menu entries. Both side navs
 * (flyout and inline) render this, so a nav rule lives here once.
 */
export function buildSideNavRoutes(input: SideNavBuildInput): Route[] {

    return input.routes
        .filter(route => !route.redirectTo)
        .filter(route => input.canLoadPath(route.path))
        .filter(route => passesRestrictions(route, input.tenantStatus))
        .map(route => withVisibleChildren(route, input))
        .map(collapseSingleChild);
}

function passesRestrictions(route: Route, tenantStatus: string | null | undefined): boolean {

    if (route?.data?.hidden) return false;
    if (!tenantStatus) return false;
    const restrictions = route.data?.restrictions as string[];
    return restrictions != null && restrictions.indexOf(tenantStatus) >= 0;
}

function withVisibleChildren(route: Route, input: SideNavBuildInput): Route {

    if (!route?.children?.length) return route;

    const legacyAt = (child: Route) => input.legacyNav.find(item => item.path === `/${route.path}/${child.path}`);

    const declared = route.children
        .filter(child => input.canLoadPath(`/${route.path}/${child.path}`))
        .filter(child => passesRestrictions(child, input.tenantStatus))
        .filter(child => !legacyAt(child)?.hidden);

    // Already authorized by the manifest, so they skip the path-based rights check above.
    const entries = input.entityEntries
        .filter(entry => entry.group === route.path)
        .sort((a, b) => a.order - b.order);

    const children = [...declared];
    for (const entry of entries) {
        // Beside its legacy item while both show, Beta-tagged so the two read as old and new. Once
        // legacy is gone from this menu the entry stands alone, untagged, after the declared items.
        const legacyIndex = children.findIndex(child => legacyAt(child)?.slug === entry.slug);
        if (legacyIndex >= 0) children.splice(legacyIndex + 1, 0, entry.route);
        else children.push(withoutBadge(entry.route));
    }

    return { ...route, children };
}

function withoutBadge(route: Route): Route {

    if (!route.data?.['badge']) return route;
    const { badge, ...data } = route.data;
    return { ...route, data };
}

function collapseSingleChild(route: Route): Route {

    if (route.children?.length !== 1) return route;

    const child = route.children[0];
    return { ...child, path: navChildLink(route, child).slice(1) };
}
